import { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { 
  Plus, FileText, Edit2, Trash2, Star, Copy, Eye, Variable,
  Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Heading1, Heading2, Heading3, Palette, Type, Receipt, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATE_VARIABLES = [
  { key: '{{invoice_number}}', label: 'Invoice Number', description: 'Unique invoice number' },
  { key: '{{client_name}}', label: 'Client Name', description: 'Full name of the client' },
  { key: '{{client_email}}', label: 'Client Email', description: 'Client email address' },
  { key: '{{client_phone}}', label: 'Client Phone', description: 'Client phone number' },
  { key: '{{event_date}}', label: 'Event Date', description: 'Date of the event' },
  { key: '{{event_type}}', label: 'Event Type', description: 'Type of event' },
  { key: '{{venue}}', label: 'Venue', description: 'Event venue name' },
  { key: '{{due_date}}', label: 'Due Date', description: 'Invoice due date' },
  { key: '{{total_amount}}', label: 'Total Amount', description: 'Total invoice amount' },
  { key: '{{paid_amount}}', label: 'Paid Amount', description: 'Amount already paid' },
  { key: '{{balance_due}}', label: 'Balance Due', description: 'Remaining balance' },
  { key: '{{line_items}}', label: 'Line Items', description: 'Itemized list of services' },
  { key: '{{discounts}}', label: 'Discounts Applied', description: 'List of applied discounts with amounts' },
  { key: '{{discount_tags}}', label: 'Discount Tags', description: 'Tags from applied discounts (e.g., "Wedding Special")' },
  { key: '{{retainer_amount}}', label: 'Retainer Amount', description: 'Initial retainer/deposit amount' },
  { key: '{{retainer_due_date}}', label: 'Retainer Due Date', description: 'Date retainer is due' },
  { key: '{{payment_schedule}}', label: 'Payment Schedule', description: 'Full payment schedule with all installments' },
  { key: '{{payment_schedule_table}}', label: 'Payment Schedule Table', description: 'Formatted table of all scheduled payments' },
  { key: '{{company_name}}', label: 'Company Name', description: 'Your DJ company name' },
  { key: '{{company_phone}}', label: 'Company Phone', description: 'Your company phone' },
  { key: '{{company_email}}', label: 'Company Email', description: 'Your company email' },
  { key: '{{company_address}}', label: 'Company Address', description: 'Your company address' },
  { key: '{{today_date}}', label: 'Today\'s Date', description: 'Current date' },
];

const EVENT_TYPES = ['Wedding', 'Corporate', 'Birthday', 'Anniversary', 'Club Event', 'Private Party', 'School Dance', 'Holiday Party', 'Other'];

const DEFAULT_TEMPLATE = `<div style="text-align: center; margin-bottom: 20px;">
<h1 style="color: #06b6d4; margin: 0;">INVOICE</h1>
<p style="color: #888; margin: 5px 0;">{{company_name}}</p>
</div>

<div style="display: flex; justify-content: space-between; margin-bottom: 20px;">
<div>
<h3 style="color: #8b5cf6; margin-bottom: 10px;">Bill To:</h3>
<p><strong>{{client_name}}</strong><br>
{{client_email}}<br>
{{client_phone}}</p>
</div>
<div style="text-align: right;">
<p><strong>Invoice #:</strong> {{invoice_number}}<br>
<strong>Date:</strong> {{today_date}}<br>
<strong>Due Date:</strong> {{due_date}}</p>
</div>
</div>

<h3 style="color: #8b5cf6;">Event Details:</h3>
<p><strong>Event:</strong> {{event_type}}<br>
<strong>Date:</strong> {{event_date}}<br>
<strong>Venue:</strong> {{venue}}</p>

<h3 style="color: #8b5cf6;">Services:</h3>
{{line_items}}

<div style="margin-top: 20px; border-top: 2px solid #8b5cf6; padding-top: 10px;">
<p style="text-align: right; font-size: 18px;"><strong>Total: {{total_amount}}</strong></p>
<p style="text-align: right;">Paid: {{paid_amount}}</p>
<p style="text-align: right; color: #06b6d4; font-size: 20px;"><strong>Balance Due: {{balance_due}}</strong></p>
</div>`;

const DEFAULT_FOOTER = `<div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
<h4>Payment Terms:</h4>
<ul>
<li>Payment is due by the date specified above</li>
<li>Accepted payment methods: Credit Card, Bank Transfer, Check</li>
<li>Late payments may incur additional fees</li>
</ul>
<p style="text-align: center; margin-top: 20px;">Thank you for your business!</p>
</div>`;

// Rich Text Editor Toolbar Component
function EditorToolbar({ onCommand, onInsertColor }) {
  const colors = ['#000000', '#06b6d4', '#8b5cf6', '#ec4899', '#ef4444', '#f59e0b', '#10b981'];
  
  return (
    <div className="flex flex-wrap gap-1 p-2 bg-muted/30 rounded-t-lg border-b border-border">
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('bold')} title="Bold">
        <Bold className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('italic')} title="Italic">
        <Italic className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('underline')} title="Underline">
        <Underline className="w-4 h-4" />
      </Button>
      <div className="w-px h-6 bg-border mx-1 self-center" />
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('formatBlock', '<h1>')} title="Heading 1">
        <Heading1 className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('formatBlock', '<h2>')} title="Heading 2">
        <Heading2 className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('formatBlock', '<h3>')} title="Heading 3">
        <Heading3 className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('formatBlock', '<p>')} title="Paragraph">
        <Type className="w-4 h-4" />
      </Button>
      <div className="w-px h-6 bg-border mx-1 self-center" />
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('insertUnorderedList')} title="Bullet List">
        <List className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('insertOrderedList')} title="Numbered List">
        <ListOrdered className="w-4 h-4" />
      </Button>
      <div className="w-px h-6 bg-border mx-1 self-center" />
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('justifyLeft')} title="Align Left">
        <AlignLeft className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('justifyCenter')} title="Align Center">
        <AlignCenter className="w-4 h-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onCommand('justifyRight')} title="Align Right">
        <AlignRight className="w-4 h-4" />
      </Button>
      <div className="w-px h-6 bg-border mx-1 self-center" />
      <div className="flex items-center gap-1">
        <Palette className="w-4 h-4 text-muted-foreground" />
        {colors.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => onInsertColor(color)}
            className="w-5 h-5 rounded border border-white/20 hover:scale-110 transition-transform"
            style={{ backgroundColor: color }}
            title={`Text color: ${color}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function InvoiceTemplatesPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showVariables, setShowVariables] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: DEFAULT_TEMPLATE,
    footer_content: DEFAULT_FOOTER,
    event_types: []
  });
  
  const editorRef = useRef(null);
  const footerEditorRef = useRef(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/invoice-templates`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to fetch invoice templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  }, []);

  const handleInsertColor = useCallback((color) => {
    document.execCommand('foreColor', false, color);
    editorRef.current?.focus();
  }, []);

  const handleFooterCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    footerEditorRef.current?.focus();
  }, []);

  const handleFooterInsertColor = useCallback((color) => {
    document.execCommand('foreColor', false, color);
    footerEditorRef.current?.focus();
  }, []);

  const insertVariable = (variable) => {
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (editorRef.current.contains(range.commonAncestorContainer)) {
          document.execCommand('insertText', false, variable);
          return;
        }
      }
      // Fallback: append to end
      editorRef.current.innerHTML += variable;
    }
    setShowVariables(false);
  };

  const handleCreateOrUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    const content = editorRef.current?.innerHTML || formData.content;
    const footer_content = footerEditorRef.current?.innerHTML || formData.footer_content;
    
    try {
      if (editingTemplate) {
        await axios.put(`${API}/invoice-templates/${editingTemplate.id}`, {
          ...formData,
          content,
          footer_content
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Invoice template updated');
      } else {
        await axios.post(`${API}/invoice-templates`, {
          ...formData,
          content,
          footer_content
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Invoice template created');
      }
      
      setShowCreateDialog(false);
      setEditingTemplate(null);
      setFormData({
        name: '',
        description: '',
        content: DEFAULT_TEMPLATE,
        footer_content: DEFAULT_FOOTER,
        event_types: []
      });
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save invoice template');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      content: template.content,
      footer_content: template.footer_content || DEFAULT_FOOTER,
      event_types: template.event_types || []
    });
    setShowCreateDialog(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this invoice template?')) return;
    
    try {
      await axios.delete(`${API}/invoice-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Invoice template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete invoice template');
    }
  };

  const handleSetDefault = async (templateId) => {
    try {
      await axios.post(`${API}/invoice-templates/${templateId}/set-default`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Default invoice template updated');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to set default template');
    }
  };

  const handleDuplicate = async (template) => {
    try {
      await axios.post(`${API}/invoice-templates`, {
        name: `${template.name} (Copy)`,
        description: template.description,
        content: template.content,
        footer_content: template.footer_content,
        event_types: []
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Invoice template duplicated');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const toggleEventType = (eventType) => {
    setFormData(prev => ({
      ...prev,
      event_types: prev.event_types.includes(eventType)
        ? prev.event_types.filter(e => e !== eventType)
        : [...prev.event_types, eventType]
    }));
  };

  const openCreateDialog = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      content: DEFAULT_TEMPLATE,
      footer_content: DEFAULT_FOOTER,
      event_types: []
    });
    setShowCreateDialog(true);
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
                <Receipt className="w-8 h-8 text-neon-blue" />
                Invoice Templates
              </h1>
              <p className="text-muted-foreground mt-1">Design invoice templates and assign them to event types</p>
            </div>
            <Button onClick={openCreateDialog} className="bg-neon-blue hover:bg-neon-blue/80 text-black" data-testid="create-invoice-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </div>

          {/* Templates Grid */}
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading templates...</div>
          ) : templates.length === 0 ? (
            <Card className="glass-card border-glass-border">
              <CardContent className="py-12 text-center">
                <Receipt className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-xl font-semibold mb-2">No Invoice Templates Yet</h3>
                <p className="text-muted-foreground mb-4">Create your first invoice template to customize how invoices appear</p>
                <Button onClick={openCreateDialog} className="bg-neon-blue hover:bg-neon-blue/80 text-black">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {templates.map((template) => (
                <Card 
                  key={template.id} 
                  className={`glass-card border-glass-border hover:border-neon-blue/50 transition-colors ${template.is_default ? 'ring-2 ring-neon-blue' : ''}`}
                  data-testid={`invoice-template-${template.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <FileText className="w-5 h-5 text-neon-blue" />
                          {template.name}
                          {template.is_default && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-neon-blue/20 text-neon-blue">Default</span>
                          )}
                        </CardTitle>
                        {template.description && (
                          <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Event Types */}
                    <div className="mb-4">
                      <Label className="text-xs text-muted-foreground">Assigned Event Types:</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {template.event_types && template.event_types.length > 0 ? (
                          template.event_types.map(type => (
                            <span key={type} className="px-2 py-0.5 text-xs rounded-full bg-neon-purple/20 text-neon-purple flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {type}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">All event types (General)</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => { setPreviewTemplate(template); setShowPreviewDialog(true); }}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => handleEdit(template)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => handleDuplicate(template)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Duplicate
                      </Button>
                      {!template.is_default && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleSetDefault(template.id)}
                          className="text-yellow-500 hover:text-yellow-400"
                        >
                          <Star className="w-4 h-4 mr-1" />
                          Set Default
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => handleDelete(template.id)}
                        className="text-red-500 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-card border-glass-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-neon-blue" />
              {editingTemplate ? 'Edit Invoice Template' : 'Create Invoice Template'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Template Name & Description */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Template Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Wedding Invoice"
                  className="bg-muted/20 border-glass-border"
                  data-testid="invoice-template-name"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this template"
                  className="bg-muted/20 border-glass-border"
                />
              </div>
            </div>

            {/* Event Type Assignment */}
            <div>
              <Label className="mb-2 block">Assign to Event Types</Label>
              <p className="text-sm text-muted-foreground mb-3">Select which event types should use this template. Leave empty for a general template.</p>
              <div className="flex flex-wrap gap-3">
                {EVENT_TYPES.map(eventType => (
                  <label key={eventType} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.event_types.includes(eventType)}
                      onCheckedChange={() => toggleEventType(eventType)}
                    />
                    <span className="text-sm">{eventType}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Variables Panel */}
            <div>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowVariables(!showVariables)}
                className="border-glass-border"
              >
                <Variable className="w-4 h-4 mr-2" />
                {showVariables ? 'Hide Variables' : 'Show Available Variables'}
              </Button>
              
              {showVariables && (
                <div className="mt-3 p-4 rounded-lg bg-muted/30 border border-glass-border">
                  <p className="text-sm text-muted-foreground mb-3">Click a variable to insert it into the editor:</p>
                  <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {TEMPLATE_VARIABLES.map((variable) => (
                      <button
                        key={variable.key}
                        type="button"
                        onClick={() => insertVariable(variable.key)}
                        className="text-left p-2 rounded-lg bg-background/50 hover:bg-neon-blue/10 border border-transparent hover:border-neon-blue/30 transition-colors"
                      >
                        <code className="text-neon-blue text-xs">{variable.key}</code>
                        <p className="text-xs text-muted-foreground mt-1">{variable.description}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Main Content Editor */}
            <div>
              <Label className="mb-2 block">Invoice Body Content</Label>
              <EditorToolbar onCommand={handleCommand} onInsertColor={handleInsertColor} />
              <div
                ref={editorRef}
                contentEditable
                className="min-h-[300px] p-4 bg-white text-black rounded-b-lg border border-t-0 border-border focus:outline-none focus:ring-2 focus:ring-neon-blue"
                style={{ lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: formData.content }}
                onBlur={(e) => setFormData(prev => ({ ...prev, content: e.target.innerHTML }))}
              />
            </div>

            {/* Footer Content Editor */}
            <div>
              <Label className="mb-2 block">Footer / Payment Terms</Label>
              <EditorToolbar onCommand={handleFooterCommand} onInsertColor={handleFooterInsertColor} />
              <div
                ref={footerEditorRef}
                contentEditable
                className="min-h-[150px] p-4 bg-white text-black rounded-b-lg border border-t-0 border-border focus:outline-none focus:ring-2 focus:ring-neon-blue"
                style={{ lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: formData.footer_content }}
                onBlur={(e) => setFormData(prev => ({ ...prev, footer_content: e.target.innerHTML }))}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-glass-border">
                Cancel
              </Button>
              <Button onClick={handleCreateOrUpdate} className="bg-neon-blue hover:bg-neon-blue/80 text-black" data-testid="save-invoice-template-btn">
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-card border-glass-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-neon-blue" />
              Preview: {previewTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          
          {previewTemplate && (
            <div className="bg-white text-black p-8 rounded-lg">
              <div dangerouslySetInnerHTML={{ __html: previewTemplate.content }} />
              {previewTemplate.footer_content && (
                <div dangerouslySetInnerHTML={{ __html: previewTemplate.footer_content }} />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
